import type { AppData } from '../../data/types';
import { createRng } from '../../core/rng';
import { worldToLayout } from '../../core/coords';
import { createGroundElevation } from '../../site/groundElevation';
import type { PlannedLots } from '../fabric/fabricLots';
import { houseFloorHeight } from '../fabric/foundation';
import { buildHouseOnLot } from '../fabric/house';
import { lotFrame } from '../fabric/lotFrame';
import { lotKey } from '../fabric/lotKey';
import { partyWallsOf } from '../fabric/partyWalls';
import { houseStreetsOf } from '../fabric/houseStreets';
import { blockKeysOf } from '../fabric/painted/blockSides';
import { paintedRoofMaterials } from '../fabric/painted/paintedRoofs';
import { woodcutGables } from '../fabric/woodcut/woodcutGables';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { OPENING } from '../fabric/openings';
import type { DetailPose } from '../fabric/detailSink';
import type { Vec2, Vec3 } from '../../geo/types';
import type { LifeEntrance, LitWindow } from './types';

/*
 * How many of the street-front houses show a candle after dark, at the evening's most
 * (time/daylight.ts burningShare takes it down through the night). An estimate: no source counts
 * them. It was 0.12, and the user asked for many more (7 October 2026).
 */
export const LIT_HOUSE_SHARE = 0.62;

/* A window is in the front wall when it looks within 20 degrees of the way the house faces. */
const FRONT_WINDOW_COS = Math.cos((20 * Math.PI) / 180);

/* Only a cross window's upper lights are glazed; the lower are shut behind their shutters at night
   (openingDetail.ts LEADED_LIGHT), so the candle shows through the upper part alone. */
function litWindow(pose: DetailPose, from: number, groundY: number): LitWindow {
  const { origin, right, width, height } = pose,
    glazed = height * (1 - OPENING.transomAt),
    up = height * OPENING.transomAt + glazed / 2;
  /* A skewed plot's frame scales the vector out of its wall with the wall; the glow needs the
     direction alone. */
  const reach = Math.hypot(pose.outward[0], pose.outward[2]),
    outward: Vec3 = [pose.outward[0] / reach, 0, pose.outward[2] / reach];
  return {
    position: [
      origin[0] + (right[0] * width) / 2 + outward[0] * 0.08,
      /* A house is built from its own floor and then stood on its foundation (fabricFeature.ts). */
      groundY + origin[1] + up,
      origin[2] + (right[2] * width) / 2 + outward[2] * 0.08,
    ],
    outward,
    width,
    height: glazed,
    from,
  };
}

/** A house's street door: the middle of its threshold and the way out of it. */
export interface FrontDoor {
  readonly position: Vec2;
  readonly outward: Vec2;
  readonly entrance?: LifeEntrance;
}

/** Bake positions from the actual rendered openings, including timber setbacks and foundations. */
export function collectLifeEntrances(
  data: AppData,
  lots: PlannedLots,
  seed: number
): {
  readonly entrances: readonly LifeEntrance[];
  readonly windows: readonly LitWindow[];
  /** The street door of every ordinary house, for the crowd that lives behind it (crowd/). */
  readonly doors: readonly FrontDoor[];
} {
  const all = [...lots.houses, ...lots.backhouses];
  const rng = createRng(seed).fork('fabric').fork('fabric');
  const party = partyWallsOf(all),
    streets = houseStreetsOf(all, party, data.layout);
  const keys = blockKeysOf(all, streets);
  const roofs = paintedRoofMaterials(all, keys, rng),
    gables = woodcutGables(all, keys, rng);
  const elevation = createGroundElevation(data.layout);
  const entrances: LifeEntrance[] = [];
  const windows: LitWindow[] = [];
  const doors: FrontDoor[] = [];
  const candles = createRng(seed).fork('lit-windows');
  let eligible = 0;
  all.forEach((lot, i) => {
    const front = streets[i]?.front;
    // Bespoke houses keep their own openings and access.
    if (
      lot.kind !== 'house' ||
      lot.reconstruction?.id.startsWith('hoge-huis') ||
      lot.reconstruction?.id.includes('claverblat')
    )
      return;
    const candle = candles.fork(lotKey(lot)),
      lit = candle.next() < LIT_HOUSE_SHARE;
    /* A house that stands on the water has no street door for the cast, but a candle burns in
       it as in any other. */
    if (!front && !lit) return;
    // The cast needs a home for each of its citizens on a broad front; every third left too few.
    const homed = !!front && !(front.width !== null && front.width < 5.5) && ++eligible % 2 === 0;
    const frame = lotFrame(lot),
      builder = createMeshBuilder(),
      found: LifeEntrance[] = [],
      glazed: DetailPose[] = [];
    const groundY = houseFloorHeight(frame, elevation);
    let shop = false,
      bread = false;
    const geometry = buildHouseOnLot(
      builder,
      lot,
      data.palette,
      rng.fork(lotKey(lot)),
      party[i]!,
      {
        add(kind, pose) {
          shop ||= kind === 'shop';
          bread ||= kind === 'loaves';
          if (kind === 'window') {
            /* A skewed plot's frame scales and turns the vector out of its front wall, so the
               front is told by its direction alone, and within a few degrees. */
            const out = worldToLayout(pose.outward),
              along = out[0] * frame.facing[0] + out[1] * frame.facing[1];
            if (along > FRONT_WINDOW_COS * Math.hypot(out[0], out[1])) glazed.push(pose);
          }
          if (!front || kind !== 'doorLeaf' || pose.origin[1] > 0.1 || pose.width < 0.95) return;
          const outward = worldToLayout(pose.outward),
            right = worldToLayout(pose.right);
          if (outward[0] * frame.facing[0] + outward[1] * frame.facing[1] < 0.99) return;
          const centre = worldToLayout([
            pose.origin[0] + (pose.right[0] * pose.width) / 2,
            pose.origin[1],
            pose.origin[2] + (pose.right[2] * pose.width) / 2,
          ]);
          found.push({
            id: `door:${lotKey(lot)}`,
            lotKey: lotKey(lot),
            position: centre,
            outward,
            right,
            width: pose.width,
            height: pose.height,
            groundY,
            stepReach: 0,
            stepHeight: 0,
            streetId: front.id,
            kind: 'home',
          });
        },
      },
      streets[i],
      { roof: roofs[i] ?? null, gable: gables[i] ?? null }
    );
    builder.finish().dispose();
    const complete = found.map((entry): LifeEntrance => ({
      ...entry,
      stepReach: geometry.frontStep?.reach ?? 0,
      stepHeight: geometry.frontStep?.height ?? 0,
      kind: bread ? 'bakery' : shop ? 'shop' : 'home',
    }));
    if (complete[0])
      doors.push({
        position: complete[0].position,
        outward: complete[0].outward,
        entrance: complete[0],
      });
    if (lit && glazed.length) windows.push(litWindow(candle.pick(glazed), candle.next(), groundY));
    if (!homed) return;
    entrances.push(...complete);
  });
  return { entrances, windows, doors };
}
